<template>
    <section class="mx-2 my-4" :aria-labelledby="titleId">
        <BaseCard theme="default" radius="lgr" padding="md" class="w-full min-w-0 max-w-[600px]">
            <div class="flex items-start gap-3">
                <span v-if="!actions.length" class="goal-icon" aria-hidden="true"><DevelopmentGoalHourglass class="size-5" /></span>
                <div class="min-w-0 grow">
                    <h2 :id="titleId" class="text-pretty text-ink" :class="actions.length ? 'body-small-semibold' : 'nadia-chat-bold'">{{ goal.title }}</h2>
                    <p v-if="draft && goal.achievement_criteria" class="body-small mt-1 text-ink-muted">{{ goal.achievement_criteria }}</p>
                    <p class="scaffold-details-small mt-1 text-ink-muted">
                        {{
                            draft
                                ? t("myDevelopment.goalDraft")
                                : saved
                                  ? t("myDevelopment.goalSaved")
                                  : goal.is_focused
                                    ? t("myDevelopment.focusedGoal")
                                    : t("myDevelopment.savedGoal")
                        }}
                        <template v-if="!draft && !saved && goal.completed_action_count != null && goal.committed_action_count != null">{{
                            t("myDevelopment.actionsCompleteSuffix", { completed: goal.completed_action_count, total: goal.committed_action_count })
                        }}</template>
                        <template v-if="goal.target_date">{{ t("myDevelopment.dueSuffix", { date: formatGoalDate(goal.target_date) }) }}</template>
                        <template v-if="!draft && !actions.length && !goal.week_actions_partial">{{ t("myDevelopment.noWeekActionsSuffix") }}</template>
                    </p>
                </div>
            </div>
            <ul v-if="actions.length" class="body-small mt-3 list-disc space-y-2 pl-6 text-ink">
                <li v-for="action in actions" :key="action.id">{{ action.title }}</li>
            </ul>
            <p v-if="goal.week_actions_partial" class="body-small mt-2 text-ink-muted">{{ t("myDevelopment.someWeekActionsOmitted") }}</p>
        </BaseCard>
    </section>
</template>

<script setup lang="ts">
import BaseCard from "~vue/components/BaseCard.vue";
import DevelopmentGoalHourglass from "~vue/icons/DevelopmentJourneyHourglass.vue";
import { formatDate } from "~vue/utils/formatDate";
import { DateTime } from "luxon";
import { useId } from "vue";
import { useI18n } from "vue-i18n";

defineProps<{
    goal: {
        title: string;
        achievement_criteria?: string;
        target_date: string | null;
        is_focused?: boolean;
        completed_action_count?: number | null;
        committed_action_count?: number | null;
        week_actions_partial?: boolean;
    };
    actions: { id: string; title: string }[];
    draft?: boolean;
    saved?: boolean;
}>();

const titleId = useId();
const { t } = useI18n();
const formatGoalDate = (date: string) => formatDate(date, DateTime.fromISO(date).year === DateTime.now().year ? "month-day" : "date");
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .goal-icon {
        @apply flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-sunken;
    }
}
</style>
